import React, { useState, useEffect } from 'react';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import Input from 'chameleon/ui-stack/ui/base/input';
import Button from 'chameleon/ui-stack/ui/base/button';
import { fymClient } from 'legacy-src/utility/GraphqlClients';
import {
  itemShareInviteCopyLink,
  checkShareInviteEmail,
} from 'legacy-src/queries';
import { toast } from 'chameleon/ui-stack/ui/toast';
import log from 'chameleon/ui-stack/utilities/log';
import { getItemDescription } from 'chameleon-src/queries';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import TagsInput from '../TagsInput';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import dayjs from 'dayjs';

const emailsIsValid = (emails) =>
  emails && !emails.match(/^([^,]+@[^,]+\.[^,]+,)*([^,]+@[^,]+\.[^,]+)$/);

const CreateSharedAssessmentModalComponent = ({
  educationPeriod,
  isLoading,
  assessmentTitle,
  description,
  emails,
  getCopyLink,
  setDescription,
  setEmails,
  subject,
  itemId,
  copyDisabled,
  onClickShare,
  onClickClose,
  hasChanges,
}) => {
  const copyProps = {
    onClick: copyDisabled ? null : getCopyLink,
    disabled: copyDisabled,
    'data-test-id': 'invite-copy-link',
  };
  const copyLink = copyDisabled ? (
    <Button {...copyProps} isSecondary={true}>
      Copied!
    </Button>
  ) : (
    <a {...copyProps} className="text-[#034088]">
      <Icon name="copy_link" /> Create Quiz Link
    </a>
  );

  const [emailErrors, setEmailErrors] = useState([]);

  const renderModalContent = () => (
    <React.Fragment>
      <div className="px-4.6 py-4.2 pb-0">
        <p className="leading-normal text-gray-800">
          The shared quiz will only be accessible to educators who have access
          to AP Classroom and are authorized for the course. When you share a
          quiz, recipients will be able to preview it. If a recipient accepts
          it, a copy is created for them. Subsequent edits you make to it are
          not reflected in their copy.
        </p>
        <label className="text-gray-700 font-medium">
          Description{' '}
          <span className="font-normal decoration-gray-700 text-base">
            (optional)
          </span>
        </label>
        <Input
          multiline
          className="mt-3"
          onChange={(e) => setDescription(e.target.value)}
          value={description}
          data-test-id="invite-description"
        />
        <p className="leading-normal text-gray-800">
          <span className="font-semibold">Share Option 1:</span> Enter College
          Board professional account email addresses to share directly within AP
          Classroom. The recipient will see the quiz in their “Shared Quizzes
          &gt; Shared with Me” view.
        </p>
        <TagsInput
          placeholder="Email, separated by comma"
          subject={subject}
          educationPeriod={educationPeriod}
          isValidTag={(tag, tagErrors, setTagErrors) => {
            return validateEmail(
              tag,
              itemId,
              educationPeriod,
              tagErrors,
              setTagErrors,
            );
          }}
          value={emails}
          tagErrors={emailErrors}
          setTagErrors={setEmailErrors}
          data-test-id="invite-emails"
          onChange={(tags) => {
            setEmails(tags);
          }}
        />
        {emailErrors.length > 0 && (
          <label className="text-red-600 mt-5 block">
            * email address is not associated with a College Board professional
            account authorized for this course
          </label>
        )}
        <div className="flex-auto flex-col justify-center py-5 pb-0 text-right">
          <Button
            disabled={
              emails === undefined ||
              emails === null ||
              emails.length === 0 ||
              emailErrors.length > 0 ||
              !hasChanges
            }
            isPrimary={emailErrors.length === 0}
            isDanger={emailErrors.length > 0}
            size="medium"
            onClick={() => {
              if (emailsIsValid(emails.join(','))) {
                setEmailErrors([]);
                return;
              }
              return onClickShare();
            }}
            data-test-id="invite-share-assessment"
          >
            Share Quiz
          </Button>
        </div>
      </div>
      <div className="border-t border-gray-300 mt-5 px-4.6 py-4.2">
        <div className="flex flex-col justify-center pb-2">
          <p className="leading-normal text-gray-800">
            <span className="font-semibold">Share Option 2:</span> Click the
            link below to generate and copy a link to the quiz to your device’s
            clipboard. You can then paste the link into an email or web page.
          </p>
        </div>
        <div className="px-4.6 py-4.2 text-right">{copyLink}</div>
      </div>
    </React.Fragment>
  );

  return (
    <ConfirmDialog
      size="medium"
      title={`Share ${assessmentTitle}`}
      onClose={onClickClose}
    >
      {isLoading ? <LoadingSpinner /> : renderModalContent()}
    </ConfirmDialog>
  );
};

const validateEmail = async (
  email,
  itemId,
  educationPeriod,
  emailErrors,
  setEmailErrors,
) => {
  const response = await fymClient.query({
    query: checkShareInviteEmail,
    fetchPolicy: 'cache-first',
    variables: {
      email,
      itemId,
      educationPeriod,
    },
  });
  if (!response.data.checkShareInviteEmail.isValid) {
    setEmailErrors([...emailErrors, email]);
  }
  return response.data.checkShareInviteEmail.isValid;
};

export const createInvite = async (
  masterSubjectId,
  period,
  itemId,
  revId,
  description,
  emails,
  copyLinkButton,
  updatedAt,
) => {
  const emailList = !emails ? null : emails;
  return await fymClient.mutate({
    mutation: itemShareInviteCopyLink,
    fetchPolicy: 'network-only',
    client: fymClient,
    variables: {
      masterSubjectId,
      period,
      itemId,
      revId,
      description,
      emails: emailList,
      copyLinkButton,
      updatedAt,
    },
  });
};

const keyToUrl = (subjectId, key) =>
  `${FYM_FRONTEND_URL}/${subjectId}/shared-quizzes/${key}`;

const handleCopyLinkResponse = (subject, promise, setCopyDisabled) =>
  promise
    .then(({ data: { itemShareInvite } }) => {
      setCopyDisabled(true);
      const url = keyToUrl(subject, itemShareInvite.itemShareInvite.shareKey);
      navigator.clipboard.writeText(url);
      toast(`${url} has been copied to your clipboard!`);
    })
    .catch((e) => {
      toast('Could not create a link, an error occurred');
      log(e);
    });

const handleShareResponse = (
  subject,
  promise,
  assessmentTitle,
  setHasChanges,
) =>
  promise
    .then(({ data: { itemShareInvite } }) => {
      setHasChanges(false);
      const url = keyToUrl(subject, itemShareInvite.itemShareInvite.shareKey);
      navigator.clipboard.writeText(url);

      toast(`"${assessmentTitle}" shared with the specified recipients`);
    })
    .catch((e) => {
      toast('Could not share the quiz, an error occurred');
      log(e);
    });

const getQuizDescription = async (quizId, closeModal) => {
  try {
    const response = await fymClient.query({
      query: getItemDescription,
      fetchPolicy: 'network-only',
      variables: {
        assessmentId: quizId,
      },
    });
    const { assessment } = (response && response.data) || {};
    return assessment && assessment.description;
  } catch (err) {
    toast('An error occured, could not fetch quiz description');
    log(err);
    closeModal();
  }
};

const CreateSharedAssessmentModal = ({
  subject,
  educationPeriod,
  itemId,
  assessmentTitle,
  revId = -1,
  onClickClose,
}) => {
  const [description, setDescription] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [emails, setEmails] = useState();
  const [copyDisabled, setCopyDisabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const setCurrentQuizDescription = async () => {
    const currentDescription = await getQuizDescription(itemId, onClickClose);
    setDescription(currentDescription);
    setIsLoading(false);
  };

  useEffect(() => {
    setCurrentQuizDescription();
  }, []);

  return (
    <CreateSharedAssessmentModalComponent
      educationPeriod={educationPeriod}
      subject={subject}
      itemId={itemId}
      assessmentTitle={assessmentTitle}
      isLoading={isLoading}
      description={description}
      setDescription={setDescription}
      emails={emails}
      onClickClose={onClickClose}
      setEmails={(value) => {
        setHasChanges(true);
        setEmails(value);
      }}
      hasChanges={hasChanges}
      copyDisabled={copyDisabled}
      onClickShare={() => {
        handleShareResponse(
          subject,
          createInvite(
            subject,
            educationPeriod,
            itemId,
            revId,
            description,
            emails,
            false,
            dayjs.utc().format(),
          ),
          assessmentTitle,
          setHasChanges,
        );
      }}
      getCopyLink={() => {
        setCopyDisabled(true);
        handleCopyLinkResponse(
          subject,
          createInvite(
            subject,
            educationPeriod,
            itemId,
            revId,
            description,
            null,
            true,
            dayjs.utc().format(),
          ),
          setCopyDisabled,
        );
      }}
    />
  );
};

export default CreateSharedAssessmentModal;
export { CreateSharedAssessmentModalComponent };
